import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * Breadcrumb component for SEO and navigation.
 * Renders semantic <nav> with proper aria-label and Schema.org BreadcrumbList JSON-LD.
 *
 * @param {Array} items - [{label, href}] - last item is current page (no link)
 * @param {string} variant - "light" (for dark backgrounds) or "default"
 * @param {string} siteUrl - base URL for schema.org absolute URLs
 */
export function Breadcrumb({ items = [], variant = 'default', siteUrl = '' }) {
  if (!items.length) return null;

  const isLight = variant === 'light';

  return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumb-nav">
      <ol
        className="flex items-center gap-1.5 text-sm flex-wrap"
        itemScope
        itemType="https://schema.org/BreadcrumbList"
      >
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <li
              key={i}
              className="flex items-center gap-1.5"
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              {i > 0 && (
                <ChevronRight className={`h-3.5 w-3.5 flex-shrink-0 ${isLight ? 'text-blue-300' : 'text-muted-foreground/50'}`} />
              )}
              {isLast ? (
                <span
                  className={`font-medium ${isLight ? 'text-white' : 'text-foreground'}`}
                  itemProp="name"
                >
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.href}
                  className={`transition-colors ${
                    isLight
                      ? 'text-blue-200 hover:text-white'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  itemProp="item"
                >
                  <span itemProp="name">{item.label}</span>
                </Link>
              )}
              <meta itemProp="position" content={String(i + 1)} />
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Generate BreadcrumbList JSON-LD for Schema.org.
 * Use inside react-helmet-async <Helmet>.
 *
 * @param {Array} items - [{label, href}]
 * @param {string} siteUrl - absolute base URL
 * @returns {string} JSON-LD string
 */
export function breadcrumbJsonLd(items, siteUrl = 'https://uretimnoktasi.com') {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.label,
      "item": `${siteUrl}${item.href}`
    }))
  });
}
